import { ROOM_CONDITION_LABELS, type RoomCondition, shownConfirmations } from '@acasa/shared'
import { newId, removeItem, type SectionProps, updateItem } from './body-utils'
import { AddButton, EntryCard, Field, TextArea, TextInput } from './fields'
import { PhotoStrip } from './photo-strip'

const CONDITIONS: { value: RoomCondition; label: string; tone?: 'default' | 'warn' }[] = [
  { value: 'ok', label: ROOM_CONDITION_LABELS.ok },
  { value: 'defects', label: ROOM_CONDITION_LABELS.defects, tone: 'warn' },
  { value: 'not_inspected', label: ROOM_CONDITION_LABELS.not_inspected },
]

export function RoomsSection({ protocol, body, patch, readOnly }: SectionProps) {
  const flagged = body.rooms.filter((room) => room.condition === 'defects').length

  return (
    <div className="space-y-3">
      <p className="text-muted text-xs">
        Jeder Raum wird bewertet. „Mängel“ öffnet die Mängelliste — nur dort erfasste Punkte
        erscheinen später im Protokoll.
        {flagged > 0 && <span className="text-danger"> {flagged} Raum/Räume mit Mängeln.</span>}
      </p>

      {body.rooms.map((room) => (
        <EntryCard
          key={room.id}
          tone={room.condition === 'defects' ? 'flagged' : 'default'}
          title={
            <input
              value={room.name}
              disabled={readOnly}
              placeholder="Raum"
              onChange={(event) =>
                patch((current) => ({
                  ...current,
                  rooms: updateItem(current.rooms, room.id, { name: event.target.value }),
                }))
              }
              className="text-ink w-full bg-transparent text-sm font-medium outline-none"
            />
          }
          onRemove={
            readOnly
              ? undefined
              : () =>
                  patch((current) => ({ ...current, rooms: removeItem(current.rooms, room.id) }))
          }
        >
          <div className="space-y-3">
            <Segment
              value={room.condition}
              disabled={readOnly}
              onChange={(condition) =>
                patch((current) => ({
                  ...current,
                  rooms: updateItem(current.rooms, room.id, { condition }),
                }))
              }
            />

            <Field label="Notiz">
              <TextArea
                rows={2}
                disabled={readOnly}
                value={room.note}
                placeholder="Zustand, Besonderheiten, Vereinbarungen"
                onChange={(event) =>
                  patch((current) => ({
                    ...current,
                    rooms: updateItem(current.rooms, room.id, { note: event.target.value }),
                  }))
                }
              />
            </Field>

            {room.condition === 'defects' && (
              <div className="border-line space-y-2 border-l-2 pl-3">
                <h4 className="text-faint text-[10px] tracking-[0.14em] uppercase">Mängel</h4>

                {room.defects.map((defect) => (
                  <div key={defect.id} className="bg-canvas border-line space-y-2 border p-2.5">
                    <div className="flex items-start gap-2">
                      <TextArea
                        rows={2}
                        disabled={readOnly}
                        value={defect.description}
                        placeholder="Was genau ist beschädigt oder fehlt?"
                        onChange={(event) =>
                          patch((current) => ({
                            ...current,
                            rooms: updateItem(current.rooms, room.id, {
                              defects: updateItem(room.defects, defect.id, {
                                description: event.target.value,
                              }),
                            }),
                          }))
                        }
                      />
                      {!readOnly && (
                        <button
                          type="button"
                          aria-label="Mangel entfernen"
                          onClick={() =>
                            patch((current) => ({
                              ...current,
                              rooms: updateItem(current.rooms, room.id, {
                                defects: removeItem(room.defects, defect.id),
                              }),
                            }))
                          }
                          className="text-faint hover:text-danger shrink-0 px-1 text-sm"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                      <Field label="Zu erledigen durch">
                        <TextInput
                          disabled={readOnly}
                          value={defect.responsibility}
                          placeholder="Vermieter / Mieter"
                          onChange={(event) =>
                            patch((current) => ({
                              ...current,
                              rooms: updateItem(current.rooms, room.id, {
                                defects: updateItem(room.defects, defect.id, {
                                  responsibility: event.target.value,
                                }),
                              }),
                            }))
                          }
                        />
                      </Field>
                      <Field label="Frist">
                        <TextInput
                          disabled={readOnly}
                          value={defect.dueDate}
                          placeholder="z. B. 30.09.2026"
                          onChange={(event) =>
                            patch((current) => ({
                              ...current,
                              rooms: updateItem(current.rooms, room.id, {
                                defects: updateItem(room.defects, defect.id, {
                                  dueDate: event.target.value,
                                }),
                              }),
                            }))
                          }
                        />
                      </Field>
                    </div>
                  </div>
                ))}

                {!readOnly && (
                  <AddButton
                    onClick={() =>
                      patch((current) => ({
                        ...current,
                        rooms: updateItem(current.rooms, room.id, {
                          defects: [
                            ...room.defects,
                            { id: newId(), description: '', responsibility: '', dueDate: '' },
                          ],
                        }),
                      }))
                    }
                  >
                    Mangel erfassen
                  </AddButton>
                )}
              </div>
            )}

            <PhotoStrip
              protocol={protocol}
              section="room"
              refKey={room.id}
              readOnly={readOnly}
              label="Foto zum Raum"
            />
          </div>
        </EntryCard>
      ))}

      {!readOnly && (
        <AddButton
          onClick={() =>
            patch((current) => ({
              ...current,
              rooms: [
                ...current.rooms,
                { id: newId(), name: '', condition: 'ok', note: '', defects: [], photoIds: [] },
              ],
            }))
          }
        >
          Raum hinzufügen
        </AddButton>
      )}
    </div>
  )
}

/** Local wrapper so the condition control keeps its warn tone. */
function Segment({
  value,
  onChange,
  disabled,
}: {
  value: RoomCondition
  onChange: (value: RoomCondition) => void
  disabled: boolean
}) {
  return (
    <div className="border-line flex border">
      {CONDITIONS.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`flex-1 px-2 py-2.5 text-xs transition-colors disabled:opacity-60 ${
              active
                ? option.tone === 'warn'
                  ? 'bg-danger text-surface font-medium'
                  : 'bg-brand text-surface font-medium'
                : 'text-muted hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function RemarksSection({ protocol, body, patch, readOnly }: SectionProps) {
  const isRental = protocol.kind !== 'sale_handover'
  // The same call the PDF makes, so the two cannot disagree about which boxes a
  // protocol has: this kind's questions while it is a draft, and on a completed
  // one only those that were actually put.
  const confirmations = shownConfirmations(protocol.kind, protocol.status, body.confirmations)

  return (
    <div className="space-y-4">
      {/*
        Each of these is a discrete legal fact somebody asks about a year
        later — a tick box answers it, a sentence buried in Bemerkungen does
        not. Which ones depends on the kind.
      */}
      {confirmations.length > 0 && (
        <fieldset>
          <legend className="text-faint mb-2 text-[10px] tracking-[0.14em] uppercase">
            Bestätigungen des Mieters
          </legend>
          <div className="border-line divide-line divide-y border">
            {confirmations.map(({ key, label, checked }) => (
              <label
                key={key}
                className={`flex items-start gap-3 px-3 py-2.5 ${
                  readOnly ? '' : 'hover:bg-canvas cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={checked}
                  onChange={(event) =>
                    patch((current) => ({
                      ...current,
                      confirmations: { ...current.confirmations, [key]: event.target.checked },
                    }))
                  }
                  className="accent-brand mt-0.5 size-4 shrink-0"
                />
                <span className="text-ink text-sm leading-snug">{label}</span>
              </label>
            ))}
          </div>
          <p className="text-faint mt-1.5 text-xs">
            Nicht angekreuzte Punkte erscheinen im PDF als offen.
          </p>
        </fieldset>
      )}
      <Field
        label="Bemerkungen"
        hint="Vereinbarungen, Einweisungen, Zustand der Wohnung insgesamt."
      >
        <TextArea
          rows={5}
          disabled={readOnly}
          value={body.remarks}
          onChange={(event) => patch((current) => ({ ...current, remarks: event.target.value }))}
        />
      </Field>

      <Field
        label={isRental ? 'Kaution / Sonstiges' : 'Sonstige Vereinbarungen'}
        hint={
          isRental
            ? 'Stand der Kaution, Nebenkostenabrechnung, Nachsendeauftrag.'
            : 'Übernahme von Inventar, offene Punkte aus dem Kaufvertrag.'
        }
      >
        <TextArea
          rows={3}
          disabled={readOnly}
          value={body.depositNote}
          onChange={(event) =>
            patch((current) => ({ ...current, depositNote: event.target.value }))
          }
        />
      </Field>

      <div>
        <h3 className="text-faint mb-1 text-[10px] tracking-[0.14em] uppercase">Weitere Fotos</h3>
        <p className="text-muted mb-2 text-xs">
          Aufnahmen, die zu keinem einzelnen Raum gehören — Außenansicht, Briefkasten, Keller.
        </p>
        <PhotoStrip protocol={protocol} section="general" refKey={null} readOnly={readOnly} />
      </div>
    </div>
  )
}
